import {
    AuthenticationResult,
    EventMessage,
    EventType,
    AuthError,
    InteractionStatus,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import React, { useEffect, useRef, useState } from "react";
import {
    service,
    factories,
    models,
    IEmbedConfiguration,
} from "powerbi-client";
import * as config from "./config";
import PowerBIDashboardContainer from "./PowerBIDashboard.styles";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";

const powerbi = new service.Service(
    factories.hpmFactory,
    factories.wpmpFactory,
    factories.routerFactory
);

// Convert an OData filter string ("Table/Column eq 'value'" or "Table/Column eq 123")
// into a structured IBasicFilter for the PowerBI SDK embed path.
const buildSdkFilter = (
    filterString: string | undefined
): models.IBasicFilter | null => {
    if (!filterString) return null;
    const match = filterString.match(/^(\w+)\/(\w+)\s+eq\s+(.+)$/);
    if (!match) return null;
    const [, table, column, rawValue] = match;
    const isQuoted = rawValue.startsWith("'") && rawValue.endsWith("'");
    const value = isQuoted ? rawValue.slice(1, -1) : parseFloat(rawValue);
    if (typeof value === "number" && isNaN(value)) return null;
    return new models.BasicFilter({ table, column }, "In", value).toJSON();
};

interface Props {
    reportId: string;
    workspaceId: string;
    filterString?: string;
}

export default function PowerBIDashboard({
    reportId,
    workspaceId,
    filterString,
}: Props) {
    const { instance, accounts, inProgress } = useMsal();
    const [accessToken, setAccessToken] = useState<string>("");
    const [embedUrl, setEmbedUrl] = useState<string>("");
    const [errors, setErrors] = useState<string[]>([]);

    const reportContainerRef = useRef<HTMLDivElement>(null);
    const reportContainer = useRef<HTMLElement | null>(null);

    // Refresh user permissions
    const tryRefreshUserPermissions = async () => {
        try {
            const response = await fetch(
                config.powerBiApiUrl + "v1.0/myorg/RefreshUserPermissions",
                {
                    headers: {
                        Authorization: "Bearer " + accessToken,
                    },
                    method: "POST",
                }
            );

            if (response.ok) {
                console.log("User permissions refreshed successfully.");
            } else {
                if (response.status === 429) {
                    console.error(
                        "Permissions refresh will be available in up to an hour."
                    );
                } else {
                    console.error(response);
                }
            }
        } catch (error) {
            console.error("Failure in making API call." + error);
        }
    };

    // Power BI REST API call to get the embed URL of the report
    const getEmbedUrl = async () => {
        if (!accessToken) return;

        try {
            const response = await fetch(
                config.powerBiApiUrl +
                    "v1.0/myorg/groups/" +
                    workspaceId +
                    "/reports/" +
                    reportId,
                {
                    headers: {
                        Authorization: "Bearer " + accessToken,
                    },
                    method: "GET",
                }
            );

            if (response.ok) {
                const body = await response.json();
                const newEmbedUrl = body["embedUrl"];
                setEmbedUrl(newEmbedUrl);
            } else {
                const body = await response.json();
                const errorMessages: string[] = [];
                errorMessages.push(
                    "Error occurred while fetching the embed URL of the report"
                );
                errorMessages.push(
                    "Request Id: " + response.headers.get("requestId")
                );
                errorMessages.push(
                    "Error " + response.status + ": " + body.error.code
                );
                setErrors(errorMessages);
            }
        } catch (error) {
            setErrors(["Error in making the API call: " + error]);
        }
    };

    // Authenticate to get access token
    const authenticate = () => {
        if (errors.length > 0) {
            return;
        }

        const isAuthenticated = accounts.length > 0;

        const eventCallback = instance.addEventCallback(
            (message: EventMessage) => {
                if (
                    message.eventType === EventType.LOGIN_SUCCESS &&
                    !accessToken
                ) {
                    const payload = message.payload as AuthenticationResult;
                    setAccessToken(payload.accessToken);
                    tryRefreshUserPermissions();
                }
            }
        );

        const loginRequest = {
            scopes: config.scopeBase,
            account: accounts[0],
        };

        if (!isAuthenticated && inProgress === InteractionStatus.None) {
            instance.loginRedirect(loginRequest);
        } else if (isAuthenticated && accessToken && !embedUrl) {
            getEmbedUrl();
            instance.removeEventCallback(eventCallback!!);
        } else if (
            isAuthenticated &&
            !accessToken &&
            !embedUrl &&
            inProgress === InteractionStatus.None
        ) {
            instance
                .acquireTokenSilent(loginRequest)
                .then((response: AuthenticationResult) => {
                    setAccessToken(response.accessToken);
                    getEmbedUrl();
                })
                .catch((error: AuthError) => {
                    if (
                        error.errorCode === "consent_required" ||
                        error.errorCode === "interaction_required" ||
                        error.errorCode === "login_required"
                    ) {
                        instance.acquireTokenRedirect(loginRequest);
                    } else if (error.errorCode === "429") {
                        setErrors([
                            "Our Service Token Server (STS) is overloaded, please try again in sometime",
                        ]);
                    } else {
                        setErrors([
                            "There was some problem fetching the access token: " +
                                error.toString(),
                        ]);
                    }
                });
        }
    };

    // Embed the report
    const embedReport = () => {
        if (
            !reportContainer.current ||
            !accessToken ||
            !embedUrl ||
            errors.length > 0
        ) {
            return;
        }

        const sdkFilter = buildSdkFilter(filterString);
        const embedConfiguration: IEmbedConfiguration = {
            type: "report",
            tokenType: models.TokenType.Aad,
            accessToken,
            embedUrl,
            id: reportId,
            filters: sdkFilter ? [sdkFilter] : undefined,
            settings: {
                background: models.BackgroundType.Transparent,
                filterPaneEnabled: false,
            },
        };

        const report = powerbi.embed(
            reportContainer.current,
            embedConfiguration
        );

        // Clear any other loaded handler events
        report.off("loaded");

        // Triggers when a content schema is successfully loaded
        report.on("loaded", function () {
            console.log("Report load successful");
        });

        // Clear any other rendered handler events
        report.off("rendered");

        // Triggers when a content is successfully embedded in UI
        report.on("rendered", function () {
            console.log("Report render successful");
        });

        // Clear any other error handler event
        report.off("error");

        // Below patch of code is for handling errors that occur during embedding
        report.on("error", function (event: any) {
            const errorMsg = event.detail;
            console.error(errorMsg);
        });
    };

    // Component mount effect
    useEffect(() => {
        if (reportContainerRef.current) {
            reportContainer.current = reportContainerRef.current;
        }

        // User input validation
        if (workspaceId === "" || reportId === "") {
            setErrors([
                "Please assign values to workspace Id and report Id in Config.ts file",
            ]);
            return;
        }

        authenticate();
    }, [workspaceId, reportId]);

    // Effect to re-authenticate when dependencies change
    useEffect(() => {
        authenticate();
    }, [accounts, inProgress, accessToken]);

    // Effect to embed report when token and URL are available
    useEffect(() => {
        if (accessToken && embedUrl && reportContainer.current) {
            embedReport();
        }
    }, [accessToken, embedUrl, filterString]);

    // Cleanup effect
    useEffect(() => {
        return () => {
            if (reportContainer.current) {
                powerbi.reset(reportContainer.current);
            }
        };
    }, []);

    useEffect(() => {
        console.warn("Errors occurred", errors);
    }, [errors]);

    return (
        <PowerBIDashboardContainer>
            {errors.length > 0 ? (
                <div className="manualSingInContainer">
                    <iframe
                        src={`https://app.powerbi.com/reportEmbed?reportId=${reportId}&autoAuth=true&ctid=bf4450a2-bfcb-472d-8c40-1425eafb1a3b&filterPaneEnabled=false${
                            filterString
                                ? `&filter=${encodeURIComponent(filterString)}`
                                : ""
                        }`}
                        width="100%"
                        height="100%"
                        allowFullScreen
                        frameBorder={0}
                    />
                </div>
            ) : (
                <div
                    id="reportContainer"
                    ref={reportContainerRef}
                    className="reportContainer"
                >
                    {(!accessToken || !embedUrl) && (
                        <LoadingSpinner message="AUTHENTICATING" />
                    )}
                </div>
            )}
        </PowerBIDashboardContainer>
    );
}
